/**
 * Plain-data, JSON-shaped roster types — the authoritative *raw* state for the
 * build-time roster domain.
 *
 * Discipline (load-bearing invariant): **nothing non-JSON may enter these
 * types** — no class instances, functions, `Map`s, `Date`s. Raw state *is* its
 * own serialized form, which is exactly the property that lets commands travel
 * the wire and lets persistence be a plain JSON write. Computed stats
 * (`@lancer-vtt/rules`) are a pure derivation over this data + the compendium;
 * they are NEVER stored here, never synced, never undone.
 *
 * Addressing:
 *   - **ids** (uuid strings) for reorderable objects: pilots, mechs, loadouts.
 *   - **indices** for frame-fixed positions: mount index, slot index within a
 *     mount. The frame pins the mount layout, so a mount has no stable identity
 *     of its own — its position in the frame's `mounts` array *is* its identity.
 *
 * Mechs are nested under pilots (`PilotData.mechs`) so a later `.compcon`
 * export round-trips COMP/CON's own nesting.
 */

/** uuid identifying a pilot within a roster. */
export type PilotId = string;
/** uuid identifying a mech within its owning pilot. */
export type MechId = string;
/** uuid identifying a loadout within its owning mech. */
export type LoadoutId = string;

/**
 * The four HASE mech skills, by name. Stored as an object (not a 4-tuple) so a
 * `set_mech_skill {skill, value}` command and the budget check read naturally.
 * Each is 0–6; their sum is capped at `2 + level` (enforced in the reducer, Lane B).
 */
export type MechSkills = {
  hull: number;
  agi: number;
  sys: number;
  eng: number;
}

/**
 * One earned license rank on a pilot, keyed by **license id** (a license ≈ a frame;
 * the id is `slug(license name)` — the same derivation the compendium's synthesized
 * license catalog uses, so `"GMS"` → `"gms"`). Mirrors COMP/CON's `PilotLicense
 * { License, Rank }`. `rank` is 1–3 (the I/II/III ladder); a rank of 0 is not
 * stored — an absent entry *is* rank 0 (see `withLicenseRank`).
 */
export type LicenseRank = {
  /** Compendium license id (`slug(license name)`). */
  id: string;
  /** 1–3. The pilot's earned rank in this license. */
  rank: number;
}

/**
 * One equipped weapon slot within a mount. `weaponId` is a compendium weapon id
 * (resolved against the local Compendium, never stored inline), or `null` for an
 * empty slot. Position is by index — this object's place in `MountData.slots`.
 */
export type SlotData = {
  /** Compendium weapon id, or `null` when the slot is empty. */
  weaponId: string | null;
  /**
   * Compendium weapon-**mod** id riding the slot's weapon, or `null`/absent for an
   * unmodded weapon (one mod per weapon, mirroring COMP/CON's `MechWeapon._mod`). A
   * mod is meaningful only over a weapon — it dies with its host (cleared whenever
   * the slot's `weaponId` is cleared, and on a frame change).
   *
   * **Optional/nullable for forward-load:** a roster persisted before mods landed has
   * no `modId` key; readers treat absent as "no mod", so no schema-version migration
   * is needed (the optional-field forward-load seam, the same discipline
   * `LoadoutData.systems` uses).
   */
  modId?: string | null;
}

/**
 * One mount on a mech's loadout, mirroring a frame-defined mount. `type` is the
 * frame's mount type string ("Main" | "Heavy" | "Aux" | "Flex" | …) copied from
 * the frame at frame-selection time so the reducer can size-gate weapons without
 * re-reading the frame. The mount has no id — its index in `LoadoutData.mounts`
 * (which lines up with the frame's `mounts` array) is its identity.
 */
export type MountData = {
  /** Frame mount-type string (e.g. "Main", "Heavy", "Flex"). */
  type: string;
  /** The weapon slots this mount provides (1 for most, 2 for Flex/MainAux/etc). */
  slots: SlotData[];
}

/**
 * A named loadout: an arrangement of weapons (and, later, systems) on a mech.
 * MVP uses a single loadout per mech, but the id and array shape are kept so
 * multiple named loadouts are a later elaboration, not a migration.
 */
export type LoadoutData = {
  id: LoadoutId;
  name: string;
  /** Mounts in frame order; index lines up with the frame's `mounts` array. */
  mounts: MountData[];
  /**
   * Equipped mech **systems**, by compendium system id, in equip order (mirroring
   * COMP/CON's `MechLoadout._systems`). Systems are loadout-scoped, not mount-bound —
   * no slot addressing. SP-budget enforced in the reducer.
   *
   * **Optional/nullable for forward-load:** a roster persisted before systems landed
   * has no `systems` key; readers treat absent as `[]` (`?? []`), so no schema-version
   * migration is needed (the optional-field forward-load seam).
   */
  systems?: string[];
  /**
   * Dedicated storage for the **Improved Armament** core bonus's granted Flex mount
   * (`cb_improved_armament`). This mount is NOT part of {@link mounts} — it is composed in reactively by
   * `composedMounts` (`composedMounts.ts`) only while the owning pilot holds the bonus
   * (and the native mount count is under the RAW `<3` cap), so a toggle-off/on of the
   * core bonus never touches this field and the equipped weapon survives the round-trip
   * (the whole point of dedicated-storage-over-stored-append). Holds a `MountData` with
   * `type: "Flex"` and its Flex slot(s).
   *
   * **Optional/nullable for forward-load:** a loadout persisted before this bonus was
   * wired has no `improvedArmamentMount` key; readers treat absent as "no weapon
   * equipped yet in this bonus mount" (an empty Flex mount is synthesized on demand),
   * so no schema-version migration is needed (the same optional-field forward-load
   * seam `systems`/`modId` use).
   */
  improvedArmamentMount?: MountData;
  /**
   * Dedicated storage for the **Integrated Weapon** core bonus's granted Aux
   * mount (`cb_integrated_weapon`). Same discipline as {@link improvedArmamentMount}: NOT
   * part of {@link mounts}, composed in reactively by `composedMounts`
   * (`composedMounts.ts`) only while the owning pilot holds the bonus (no cap —
   * unlike Improved Armament's `<3`), so a toggle-off/on survives the equipped
   * weapon. Holds a `MountData` with `type: "Aux"` and its single Auxiliary slot.
   * The mount's `no_mods` RAW clause is enforced in the reducer's `attach_mod`,
   * not here — this field is storage only.
   *
   * **Optional/nullable for forward-load:** same seam as
   * {@link improvedArmamentMount} — no schema-version migration needed.
   */
  integratedWeaponMount?: MountData;
  /**
   * **Mount Retrofitting** (`cb_mount_retrofitting`) — which NATIVE mount (an index into {@link mounts})
   * was retrofitted, and its pre-retrofit `type` string, so `composedMounts` can
   * revert the presented type back to it when the bonus isn't held. Unlike
   * {@link improvedArmamentMount}/{@link integratedWeaponMount} (dedicated storage for
   * a whole extra mount), this is a small pointer — the retrofit doesn't add a mount,
   * it changes an existing native one's presented type. The actual weapon-drop (a
   * weapon that no longer fits the retrofitted Main/Aux shape) is a real mutation to
   * {@link mounts}`[mountIdx].slots` at `retrofit_mount` time, not stored here — this
   * field only carries what `composedMounts` needs to know WHICH mount to swap and
   * what to swap it BACK to.
   *
   * **Optional/nullable for forward-load:** a loadout persisted before this bonus was
   * wired has no `retrofit` key; readers treat absent as "no retrofit chosen", the same
   * optional-field forward-load seam `systems`/`modId`/`improvedArmamentMount` use.
   */
  retrofit?: {
    /** Index into {@link mounts} — the native mount that was retrofitted. */
    mountIdx: number;
    /** The mount's `type` string before the retrofit (e.g. "Heavy"), to revert to. */
    originalType: string;
  };
}

/**
 * A mech: a frame selection plus its loadout(s). `frameId` is a compendium frame
 * id; the frame's base stats and mount layout are resolved against the local
 * Compendium at compute time, never copied into raw state.
 */
export type MechData = {
  id: MechId;
  name: string;
  /** Compendium frame id, or `null` before a frame is picked. */
  frameId: string | null;
  loadouts: LoadoutData[];
  /** Index into `loadouts` of the active loadout. MVP: always 0. */
  activeLoadoutIndex: number;
  /**
   * The mech's **portrait image** — a single server-relative URL to an uploaded picture
   * (`/api/uploads/mech-portraits/<id>.<ext>`, the `file-uploading` service's GET route),
   * or **absent** to fall back to the frame's vendored chassis art (`resolveMechArtUrl` in
   * `frontend/src/lib/mechArt.ts`), or the glyph placeholder if that's also unavailable.
   * Mirrors `PilotData.image` — a single server-resolved URL string, not a pair. Set via the
   * dedicated `set_mech_image` command after the browser POSTs the file to
   * `POST /api/uploads/mech-portraits/<mechId>`. Clearing to `""` reads as "unset" (see
   * `PilotTextField`'s doc comment on the pilot side for the same convention).
   */
  image?: string;
  /**
   * Freeform, plain-text mech notes — player-owned prose, not structured data, not
   * rules-bearing. **Absent means unset**, matching `image`'s
   * convention. Set via the dedicated `set_mech_notes` command (the mech command family is
   * one command per field — see `SetMechNotesCommand`'s doc comment). Rendered as plain text
   * under `white-space: pre-wrap` via `UserText` — never fed to `RulesText`, which parses a
   * different (lancer-data HTML) grammar. Lives on the blueprint, not `PrintedMechState`, so
   * Full Repair (which deletes the `printedMechs` key) never erases it. Disclosed to other
   * players on the shared sheet (`SHARED_MECH_DISCLOSURE.notes = "shown"`, `SquadApp.tsx`) —
   * a developer ruling, not a technical default.
   */
  notes?: string;
  /**
   * The **Engineer's prototype weapon** settings chosen at Full Repair (`t_engineer`, Core p.98):
   * the weapon and damage type, the two Revision picks (rank 2+) and the rolled Limited uses.
   * **Absent means none chosen**: the printed profile, no Revisions, and the weapon's printed
   * Limited minimum. Frozen onto the talent-integrated
   * Prototype Weapon at spawn (`@lancer-vtt/rules` `applyPrototypeConfig`). Set via
   * `set_mech_prototype`. Lives on the blueprint, not `PrintedMechState`: Full Repair re-picks it,
   * it does not erase it.
   */
  prototype?: PrototypeConfig;
}

/** One of Engineer rank 2's Revision picks: Tweaked Optics, Tweaked Computer, Stripped Reactor Shielding. */
export type PrototypeRevision = "optics" | "computer" | "shielding";

/** The values of {@link PrototypeRevision}, in the rulebook's order. */
export const PROTOTYPE_REVISIONS: readonly PrototypeRevision[] = ["optics", "computer", "shielding"];

/** Engineer rank 1's weapon-type pick (Core p.98): Melee takes Threat 1, every other type Range 10. */
export type PrototypeWeaponType = "Melee" | "CQB" | "Rifle" | "Cannon" | "Launcher" | "Nexus";

/** The values of {@link PrototypeWeaponType}. */
export const PROTOTYPE_WEAPON_TYPES: readonly PrototypeWeaponType[] = ["Melee", "CQB", "Rifle", "Cannon", "Launcher", "Nexus"];

/**
 * A damage type a player picks for a weapon printed Variable: the Engineer's prototype (Core p.98)
 * and the Set Damage Type pilot weapons (the Signatures, Core p.113), in place of the per-attack pick.
 */
export type ChosenDamageType = "kinetic" | "energy" | "explosive";

/** The values of {@link ChosenDamageType}. */
export const CHOSEN_DAMAGE_TYPES: readonly ChosenDamageType[] = ["kinetic", "energy", "explosive"];

/**
 * {@link MechData.prototype}: the weapon type and damage type (rank 1+), the Revision picks (two,
 * distinct; rank 2+) and the rolled Limited uses. Each is optional: an absent type keeps both of the
 * printed Threat 1 and Range 10 and offers every Shielding area; an absent damage type keeps the
 * per-attack Variable pick.
 */
export type PrototypeConfig = {
  weaponType?: PrototypeWeaponType;
  damageType?: ChosenDamageType;
  revisions?: PrototypeRevision[];
  /** The Limited roll (1d6+2, or 2d6 at rank 3), typed in by the player. A whole number ≥ 1. */
  limitedUses?: number;
  /**
   * A Full Repair has happened since {@link limitedUses} was rolled ("each time you perform a Full
   * Repair, reroll", Core p.98): set by `full_repair` / `full_repair_squad`, dropped by the next
   * roll. The old roll still freezes until then; the MECH page prompts the re-roll.
   */
  rerollDue?: true;
};

/**
 * A **printed mech's** persisted combat-wear state — a sparse
 * *delta from pristine* carried between encounters on the durable roster, keyed by
 * {@link MechId} in {@link PilotData.printedMechs}. The blueprint ({@link MechData})
 * stays a pure build; this is the only thing that diverges from "fully repaired."
 *
 * **Sparse, absent == pristine.** Every field is optional: a field is
 * stored *only when it diverges from its blueprint-derived max*, and the record
 * itself exists only when *something* diverges — an absent `printedMechs[mechId]`
 * key means the mech is at Full Repair (deploys full), and **Full Repair deletes
 * the key**. The blueprint-derived maxima (maxHP, Repair Cap *max*, structure/stress)
 * are NEVER stored here — they stay derived live from the blueprint via
 * `@lancer-vtt/rules` `compute`; only the *current/remaining* values
 * that a fight depletes live here.
 *
 * **What carries:** HP/Structure/Stress (the consequence tracks the
 * in-combat engine damages) and **Repair Cap remaining**. Heat / statuses / burn /
 * overcharge are deliberately NOT here — a between-encounter Rest clears them, so
 * deploy keeps seeding `heat: 0` and no statuses.
 */
export type PrintedMechState = {
  /** Current HP; absent ⇒ full (`maxHp`). */
  hp?: number;
  /** Current structure; absent ⇒ full (`maxStructure`). */
  structure?: number;
  /** Current stress; absent ⇒ full (`maxStress`). */
  stress?: number;
  /** Repair Cap *remaining*; absent ⇒ full (the computed Repair Cap max, `repcap`). */
  repairsRemaining?: number;
  /**
   * Total-destruction marker — absent ⇒ the mech
   * survived the encounter (the ordinary attrition case above). `"wreck"` = an
   * ordinary destruction (RAW p.82: repairable via the pooled 4-Repair restore,
   * `restore_destroyed_mech`, or Full Repair). `"vaporized"` = a reactor-meltdown
   * annihilation (RAW p.81: nothing remains — only `reprint` clears it, Full
   * Repair rejects). While `destroyed` is present, `hp`/`structure`/`stress`
   * stay absent (meaningless mid-destruction — a restore sets them by RAW
   * regardless of what they were at the moment of death); `repairsRemaining`
   * still tracks normally (the pooled restore draws against it).
   */
  destroyed?: "wreck" | "vaporized";
  /**
   * **Eject persistence marker** (the exact `destroyed`-field pattern above): absent ⇒ no
   * outstanding Eject lockout (the ordinary case). `true` ⇒ this
   * mech's pilot ejected and the mech hasn't been Full Repaired since — `spawnMech`/deploy
   * re-seeds the in-encounter Impaired status + the Eject-lockout resource
   * (`EJECT_LOCKOUT_RESOURCE`) from this flag so the restriction survives a commit/redeploy
   * round-trip. `full_repair` clears it for free (it deletes the whole sparse record, this
   * field included) — mirrors RAW p.70's "until you Full Repair" duration exactly.
   */
  ejectSpent?: true;
  /**
   * **Destroyed-item persistence** — the destroyed
   * instance keys (bare keys, e.g. `"0:0"` for a weapon-mount slot, `"system:1"` for a
   * system — the same key {@link "./encounter/types".destroyedItemResourceKey} namespaces
   * as `destroyed:<key>` on a live combatant's `current.resources`) carried between
   * encounters. Absent/omitted ⇒ no destroyed items (the ordinary case, and every
   * record without one). `spawnMech` re-seeds the runtime `destroyed:<key>` markers from
   * this list on deploy, alongside the {@link ejectSpent}/track re-seed above. `spend_repairs`
   * `track: "item"` removes one key (1 Repair); `full_repair`'s whole-record delete clears
   * every destroyed item for free, same as every other field here.
   *
   * **Dropped on a wreck/vaporized commit** (see `destroyed` above): a mech
   * that ends the fight destroyed has its items go moot along with the rest of the attrition
   * track — the commit extractor never populates this field alongside `destroyed`, mirroring
   * the track fields' own drop.
   */
  destroyedItems?: string[];
  /**
   * **Limited-weapon consumed-charge persistence** (`rest_limited_charge_replenish` —
   * t_grease_monkey:1's Unsanctioned Capacity Upgrade:
   * "While resting, you can spend 2 Repairs to replenish 1 use of all Limited weapons and
   * systems"). A sparse map from a weapon's bare instance key (the same key
   * {@link "./encounter/types".weaponInstanceKey} mints, which is also the
   * `current.affordanceState` slot it is read off — mirrors {@link destroyedItems}'s bare-key
   * convention) to how many of its `Limited {N}` charges are currently spent. Absent/omitted
   * key ⇒ 0 consumed (full charges, the ordinary case — mirrors every other sparse field
   * here). **Scope note**: covers Limited WEAPONS only — a Limited SYSTEM (a
   * granted action whose `Limited {N}` rides its frozen affordance record's `limited` pool,
   * `decisions/0202`) rides the **separate** {@link limitedSystemUsed} field instead. An ordinary
   * RAW "1/scene" ability is a `cap` rate, never a `limited` pool, so this field and
   * `limitedSystemUsed` are never conflated even though `spend_repairs track:"limited"`
   * replenishes both at once.
   *
   * **Persisted across a commit/redeploy.** A Limited weapon's consumed count must NOT reset
   * to full on redeploy — that is not RAW (only a Full Repair, or this specific talent,
   * restores charges; an ordinary Rest alone does not). `commit.ts`'s extractor captures it
   * (mirrors `destroyedItems`'s capture) and `spawnMech` re-seeds
   * `current.resources[limited:<key>]` from it on deploy (mirrors `destroyedItems`'s re-seed),
   * so charge consumption carries between encounters, and `spend_repairs`
   * `track: "limited"` (2 Repairs, `printedMech.ts`'s `replenishLimitedUsed`) is the one way
   * to claw a charge back early — mirrors `track: "item"`'s shape, one field over.
   */
  limitedUsed?: Record<string, number>;
  /**
   * **Limited-SYSTEM consumed-charge persistence** (`limited_system_charge_namespace` —
   * t_grease_monkey:1's Unsanctioned Capacity Upgrade, the "…and systems" half of
   * {@link limitedUsed}'s scope note). A
   * sparse map from a Limited pool's ledger slot to how many of its `Limited {N}` charges are
   * currently spent. The slot is the record's `limited.pool` (the bare item id) when it has one, which every
   * shipped Limited system does, and otherwise the granted action's composite id
   * (`<itemId>:<actionIndex>`, the key `CombatantBase.affordances[].key` uses). Either way it is
   * NOT a weapon instance key, a different id space.
   * Absent/omitted key ⇒ 0 consumed (full charges).
   *
   * **Only entries whose frozen affordance record carries a `limited` pool are ever captured
   * here** — the extractor ({@link "./encounter/types".limitedSystemUsedCounts}) iterates the
   * frozen records and reads that field alone before persisting a
   * `current.affordanceState[limited.pool ?? key].uses` count, so an ordinary RAW "1/scene"
   * ability (a `cap` rate, with no `limited`) is never mistakenly captured — it keeps
   * resetting for free every fresh encounter ("scene = encounter" — no persistence is
   * needed for the ordinary case).
   * This is the load-bearing distinction the whole primitive threads: without it, a
   * naive blanket capture would persist an ordinary multi-use "N/scene" ability's partial
   * consumption too, and `track:"limited"`'s +1 replenish would leave it wrongly
   * short-restored on the NEXT deploy instead of the fresh-every-encounter full availability
   * RAW gives it.
   *
   * `spawnMech` re-seeds `current.affordanceState[<slot>].uses` from this field on deploy,
   * mirroring {@link limitedUsed}'s weapon re-seed one field above. `spend_repairs
   * track: "limited"` replenishes 1 charge across every consumed entry in BOTH this field
   * AND {@link limitedUsed} at once, in the same blanket spend (RAW's "all Limited weapons
   * AND systems" is one spend, not two) — `printedMech.ts`'s `replenishLimitedUsed` is reused
   * verbatim for this field, not re-derived.
   */
  limitedSystemUsed?: Record<string, number>;
}

/**
 * A pilot: identity + level + HASE allocation + its mechs.
 *
 * `owner` is the id of the actor who created the pilot; `null` for a pilot created in
 * solo/offline mode with no actor identity. It is the live, server-authoritative
 * ownership key: `canIssueRoster` (`permissions.ts`) gates every non-GM roster command
 * against it (a player may issue a command naming `pilotId` only when it owns that
 * pilot, checked via the shared `ownsPilot` predicate), enforced as a pre-`apply` wall
 * in front of the reducer — **not** "anyone can edit anything." See `permissions.ts` for
 * the model, and `backend/src/ownershipGate.integration.test.ts` for the real-websocket
 * proof that a forged cross-owner command is refused.
 */
export type PilotData = {
  id: PilotId;
  callsign: string;
  name: string;
  /** Display-name snapshot for the pilot's selected or custom background. */
  background?: string;
  /**
   * Identity and source of a selected datapack background. Absent means `background`
   * is a deliberate custom value (including a legacy string from before this field
   * existed). Keeping the name snapshot makes a missing pack readable rather than
   * silently erasing part of a pilot's history.
   */
  backgroundRef?: { id: string; packId: string };
  /**
   * The pilot's **profile image** — a single server-relative URL to an uploaded portrait
   * (`/api/uploads/pilot-portraits/<id>.<ext>`, the `file-uploading` service's GET route),
   * or **absent** to fall back to the callsign-initial glyph. Deliberately a *single*
   * server-resolved URL string, NOT COMP/CON's `portrait`/`cloud_portrait` pair — this app
   * has no cloud storage. Set via `set_pilot_text` (field `image`) after the browser POSTs
   * the file to `POST /api/uploads/pilot-portraits/<id>`; the stored string drops straight
   * into an `<img src>` (dev proxies `/api` to the backend, prod/e2e are single-origin).
   */
  image?: string;
  /**
   * Freeform, plain-text pilot notes — player-owned prose, not structured data, not
   * rules-bearing. **Absent means unset**, matching `image`'s
   * convention. Set the same field-generic way via `set_pilot_text` (field `notes`).
   * Rendered as plain text under `white-space: pre-wrap` via `UserText` — never fed to
   * `RulesText`, which parses a different (lancer-data HTML) grammar. Disclosed to other
   * players on the shared sheet (`SHARED_SHEET_DISCLOSURE.notes = "shown"`,
   * `SquadApp.tsx`) — a developer ruling, not a technical default.
   */
  notes?: string;
  /**
   * The pilot's **Bondmate** (Bonded, Core p.90: "choose another pilot ... to be your BONDMATE"):
   * another roster pilot's id, or absent for none. Set with `set_pilot_bondmate`. Frozen onto
   * the spawned mech as `Combatant.bondmatePilotId`; when both pilots' mechs are fielded, the
   * referee plants the `Bondmate` status on a Bonded one, partnered to the other. A dangling id
   * (the bondmate pilot was deleted) is tolerated and plants nothing.
   */
  bondmate?: PilotId;
  /**
   * The pilot's **carried on-foot HP** between encounters, or absent for full. Written by the
   * END COMBAT commit from a pilot combatant (`set_pilot_hp`), read by `spawnPilot`, which
   * clamps it to the pilot's current max. Set by hand at a Rest (Core p.82: "restore half of
   * their pilot's HP"); nothing applies Rest or Full Repair to it automatically.
   */
  currentHp?: number;
  /** 0–12. Drives grit (`ceil(level/2)`) and the HASE budget (`2 + level`). */
  level: number;
  mechSkills: MechSkills;
  /**
   * Owned pilot **skill triggers**: compendium skill id → chosen rank (1–3). Only
   * owned triggers appear as keys; absent means rank 0. Total ranks are capped at
   * `4 + level` (enforced in the reducer, mirroring the HASE gate), and at LL0 each key
   * is additionally capped at rank 1 — four *distinct* +2 triggers (`rankCeilingAtLevel`
   * in `budgets.ts`). Skills carry no
   * `bonuses` — a trigger only grants a flat dice bonus to *trigger rolls*, never a
   * mech stat — so they are allocation/inventory only and never flow into `compute.ts`.
   */
  skills: Record<string, number>;
  /**
   * Owned pilot **talents**: compendium talent id → chosen rank (1–3, = highest
   * unlocked rank; ranks are cumulative 1..N). Only owned talents appear as keys.
   * Total ranks are capped at `3 + level` (enforced in the reducer), and at LL0 each key
   * is additionally capped at rank 1 — three *distinct* rank-I talents
   * (`rankCeilingAtLevel` in `budgets.ts`). Talent ranks
   * may carry `bonuses` (resolved from the compendium at compute time); those that
   * name a mech stat flow into `compute.ts` pilot-scoped (later phase).
   */
  talents: Record<string, number>;
  /**
   * Earned **frame license ranks**, keyed by license id (a license ≈ a frame). A
   * pilot accrues 1 rank per level (total ranks = LL, Core p. 18) and spends them
   * taking ranks I/II/III in frame licenses. Every pilot is **seeded with a
   * max-rank GMS entry** (`licenses.ts` `GMS_LICENSE_ID`) so GMS gear passes the
   * equip gate with no special-case; GMS is free and excluded from the per-LL
   * budget. The snapshot invariant (`Σ non-GMS ranks ≤ LL`, `each ≤ 3`) is the
   * only pacing — no per-level ledger (RAW's free reallocation collapses to it).
   * Enforced in the reducer (`set_license_rank`); the budget lives in `licenses.ts`.
   */
  licenses: LicenseRank[];
  /**
   * Taken **core-bonus ids** (compendium `core_bonus` ids, `cb_`-prefixed). Core
   * bonuses are permanent, rankless picks — taken or not — so this is a flat id
   * list, not a rank map (contrast `licenses`). A pilot may take one per 3rd LL
   * (`coreBonusBudget = floor(level/3)`), each gated by a per-manufacturer
   * license-rank prerequisite (Big-Four bonuses need 3 ranks with that
   * manufacturer per bonus; GMS is always available). **Manufacturer is derived**,
   * never stored — resolved off the compendium entry's `source` at gate time (the
   * same discipline the equip gate uses). Budget + prereq enforced in the reducer
   * (`set_core_bonus`); the helpers live in `budgets.ts` + `licenses.ts`. Those
   * carrying a `bonuses` array flow into `compute.ts` pilot-scoped (a later phase).
   */
  coreBonuses: string[];
  /**
   * Taken **pilot personal-gear ids** (compendium `pilot_gear` ids, `pg_`-prefixed) —
   * the weapons/armor/gear a pilot carries on foot, distinct from the mech
   * `LoadoutData`. A flat id list with **no duplicates** (distinct picks; mirrors
   * `coreBonuses`). Per RAW (Core p. 34) pilot gear has no LL/license gate; the only
   * limit is the per-category quantity cap (2 weapon / 1 armor / 3 gear). The
   * **category is derived**, never stored — resolved off the compendium item's `type`
   * at gate time (the same discipline `coreBonuses` uses for manufacturer). Limit
   * enforced in the reducer (`add_pilot_gear`); the helpers live in `pilotGear.ts`.
   * The armor items carry `bonuses` targeting pilot on-foot stats — those resolve via
   * `@lancer-vtt/rules`' `computePilotStats` (`ComputedPilot.combat`), not here (see
   * `pilotGear.ts`'s header for the seam).
   */
  gear: string[];
  /**
   * The damage type picked for a held **Set Damage Type** pilot weapon (`tg_set_damage_type`: the
   * Light, Medium and Heavy Signature, Core p.113, "When a signature weapon is acquired, choose its
   * damage type"), keyed by its `gear` id. Set with `set_pilot_gear_damage_type`; frozen onto the
   * pilot's weapon at spawn (`freezePilotGearWeapons`). An absent key keeps the printed Variable,
   * picked per attack.
   */
  gearDamageTypes?: Record<string, ChosenDamageType>;
  mechs: MechData[];
  /**
   * **Printed-mech combat state** — the persisted between-encounter
   * wear for each mech that has diverged from Full Repair, keyed by {@link MechId} (a
   * mech ≈ a blueprint; the key is the same `MechData.id`). A **sparse** map: only mechs
   * that carry damage / spent Repair Cap appear, and within each {@link PrintedMechState}
   * only the diverged fields are stored. **Absent key == pristine / fully repaired** — a
   * mech with no entry deploys full, and Full Repair deletes its entry. Always present
   * (an empty `{}` for a pilot whose mechs are all pristine); the migration backfills it.
   * Putting it on the pilot (not on `MechData`) keeps the blueprint pure and lets the
   * spawn adapter seed from `pilotData.printedMechs` with no new args.
   */
  printedMechs: Record<MechId, PrintedMechState>;
  /**
   * The pilot's **active mech** — the one the credential surfaces and an encounter deploys by
   * default. An explicit pick (`set_active_mech`); `null`/`undefined` falls back to the first
   * mech (see `resolveActiveMechId`). A dangling id (its mech was deleted) also falls back, so
   * `delete_mech` needn't touch this field and undo restores the active mech for free.
   */
  activeMechId?: string | null;
  /** Actor id of the pilot's owner, or `null` in solo/offline mode. */
  owner: string | null;
}

/**
 * The whole roster: pilots keyed by id (an id-keyed map in memory; serialized as
 * an array on disk — see the migration seam). `schemaVersion` is the only
 * persistence seam kept: a `vN → vN+1` plain-data migration
 * pipeline, cheap insurance, no per-class Serialize/Deserialize machinery.
 */
export type RosterState = {
  schemaVersion: number;
  pilots: Record<PilotId, PilotData>;
  /**
   * When each encounter was last committed back to this roster (encounter id → ISO
   * timestamp), written by `record_encounter_commit` in the same batch as the commit's
   * `apply_printed_mech_state` commands. Sparse: absent (or no key) means never committed.
   * It exists so a re-commit can warn — commit writes absolute values, so committing an
   * encounter again overwrites any Repairs spent since. It lives here rather than on the
   * encounter so the whole commit stays one roster batch, one undo.
   */
  encounterCommits?: Record<string, string>;
  /**
   * Allied NPCs' carried wear, keyed by a GM-chosen persistent key (`"rocket"`): what a keyed
   * NPC keeps between encounters. Written by `apply_npc_wear` / `clear_npc_wear` (GM-only), read
   * by the NPC spawn when a deploy names the key. Sparse at both levels: an absent key, and an
   * absent field inside an entry, both read as pristine (`decisions/0196`).
   */
  npcWear?: Record<string, NpcWear>;
}

/**
 * One keyed NPC's carried wear: its **current** HP, Structure and Stress (absolute values, not
 * deltas), and the destroyed marker. An absent field means that track is full. A `destroyed` key
 * refuses to spawn until the GM clears it. No Repair Cap: NPCs have none.
 */
export type NpcWear = {
  hp?: number;
  structure?: number;
  stress?: number;
  destroyed?: true;
}
